import IconRound from 'assets/logos/icon-round.svg?react';

import { DownloadOutlined, InfoCircleOutlined, UploadOutlined } from '@ant-design/icons';
import ChatExplainer from 'components/chats/ChatExplainer';
import ChatsList from 'components/chats/ChatsList';
import CaregiverAvatarList from 'components/lists/CaregiverAvatarList';
import MessagingHomeV3 from 'components/messaging/MessagingHomeV3';
import { Page, PageContent } from 'components/layout/page';
import PageTopBar from 'components/navigation/PageTopBar';
import SkeletonChats from 'components/skeleton/SkeletonChats';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import { Spinner } from 'components/ui/spinner';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { isMessagingExplainerV3Enabled } from 'utils/messagingExplainerV3';
import {
  compareByLatestMessageAsc,
  useConversationsByType,
  useConversationsLoadingStatus,
  useLoadedConversationsOnce,
  useMyConversationsSplit,
} from 'hooks/useConversations';
import useScrollRestoration from 'hooks/useScrollRestoration';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routes } from 'services/RouteService';
import { ConversationDTO } from 'services/api/models/ConversationDTO';
import { AssignedConversationSegmentTypes, UnassignedConversationSegmentTypes } from 'store/conversations/types';
import { selectCurrentUser } from 'store/users/selectors';

export default function MessagingHome() {
  // Selectors
  const currentUser = useSelector(selectCurrentUser);
  const conversations = useConversationsByType('assigned');
  const unassignedConversations = useConversationsByType('unassigned');
  const myRequestsConversations = useConversationsByType('myRequest');
  const waitingForReassignmentConversations = useConversationsByType('waitingForReassignment');
  const onHoldConversations = useConversationsByType('onHold');
  const closedConversations = useConversationsByType('closed');
  const conversationLoadingStatus = useConversationsLoadingStatus();
  const loadedConversationsOnce = useLoadedConversationsOnce();
  const { needsReply: assignedNeedsReply, replied: assignedReplied } = useMyConversationsSplit(conversations);

  // Hooks
  const { t } = useTranslation();
  const navigate = useNavigate();

  // States
  const [unassignedSegmentValue, setUnassignedSegmentValue] =
    useState<UnassignedConversationSegmentTypes>('unassigned');
  const [assignedSegmentValue, setAssignedSegmentValue] = useState<AssignedConversationSegmentTypes>('assigned');
  const scrollableElement = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollableElement);

  const getConversationsListByUnassignedValue = () => {
    switch (unassignedSegmentValue) {
      // Merge "Demandes de groupe" (unassigned) and "Mes demandes" (myRequest) into a single "En attente"
      case 'unassigned':
        return {
          title: t('home.messages.waitingForMe'),
          conversations: [...(unassignedConversations || []), ...(myRequestsConversations || [])].sort(
            compareByLatestMessageAsc,
          ),
        };
      // Rename current "En attente" to "En redirection"
      case 'waitingForReassignment':
        return {
          title: t('home.messages.waitingForReassignment'),
          conversations: waitingForReassignmentConversations || [],
        };
      default:
        return {
          title: t('home.messages.waitingForMe'),
          conversations: [],
        };
    }
  };

  const getConversationsListByAssignedValue = () => {
    switch (assignedSegmentValue) {
      case 'assigned':
        return {
          title: t('chats.subtitle.assigned'),
          conversations: conversations || [],
        };
      case 'onHold':
        return {
          title: t('chats.subtitle.onHold'),
          conversations: onHoldConversations || [],
        };
      case 'closed':
        return {
          title: t('chats.subtitle.closed'),
          conversations: closedConversations || [],
        };

      default:
        return {
          title: t('chats.subtitle.assigned'),
          conversations: [],
        };
    }
  };

  function renderChats<T extends string>(params: {
    title: string;
    conversations: ConversationDTO[];
    options: { label: React.ReactNode; value: T }[];
    value: T;
    onChange: (value: T) => void;
    headerTitle?: string | null;
    content?: React.ReactNode;
  }) {
    return (
      <>
        {params.headerTitle ? <h4 className="m-3 mb-0">{params.headerTitle}</h4> : null}
        <div className="m-3">
          <ToggleGroup
            type="single"
            variant="outline"
            className="w-full"
            value={params.value}
            onValueChange={value => {
              if (value) params.onChange(value as T);
            }}
          >
            {params.options.map(option => (
              <ToggleGroupItem key={option.value} value={option.value}>
                {option.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </div>
        {conversationLoadingStatus === 'LOADING' ? (
          <SkeletonChats numberOfItems={3} />
        ) : params.content !== undefined ? (
          params.content
        ) : (
          <ChatsList conversations={params.conversations} readOnlyConvs={false} />
        )}
      </>
    );
  }

  const renderUnassignedChats = () => {
    if (!currentUser?.is_caregiver) {
      return null;
    }

    const data = getConversationsListByUnassignedValue();

    return renderChats<UnassignedConversationSegmentTypes>({
      title: data.title,
      headerTitle: t('chats.subtitle.waitingRoom'),
      conversations: data.conversations,
      options: [
        {
          label: (
            <>
              <DownloadOutlined className="mr-1" />
              {t('home.messages.waitingForMe')} ({unassignedConversations.length + myRequestsConversations.length})
            </>
          ),
          value: 'unassigned',
        },
        {
          label: (
            <>
              <UploadOutlined className="mr-1" />
              {t('home.messages.waitingForReassignment')} ({waitingForReassignmentConversations.length})
            </>
          ),
          value: 'waitingForReassignment',
        },
      ],
      value: unassignedSegmentValue,
      onChange: value => setUnassignedSegmentValue(value),
    });
  };

  const renderAssignedChats = () => {
    if (!currentUser?.is_caregiver) {
      return conversationLoadingStatus === 'LOADING' ? (
        <SkeletonChats numberOfItems={3} />
      ) : (
        <ChatsList conversations={conversations} title={null} readOnlyConvs={false} />
      );
    }

    const data = getConversationsListByAssignedValue();

    const assignedContent =
      assignedSegmentValue === 'assigned' ? (
        <div>
          <ChatsList conversations={assignedNeedsReply} readOnlyConvs={false} />
          <div className="my-1 flex items-center gap-4 px-4">
            <Separator className="flex-1" />
            <span className="shrink-0 text-sm text-muted-foreground">{t('chats.subtitle.replied')}</span>
            <Separator className="flex-1" />
          </div>
          <ChatsList conversations={assignedReplied} readOnlyConvs={false} />
        </div>
      ) : undefined;

    return renderChats<AssignedConversationSegmentTypes>({
      title: data.title,
      conversations: data.conversations,
      options: [
        { label: `${t('chats.subtitle.assigned')}`, value: 'assigned' },
        { label: `${t('chats.subtitle.onHold')} (${onHoldConversations.length})`, value: 'onHold' },
        {
          label: `${t('chats.subtitle.closed')}`,
          value: 'closed',
        },
      ],
      // Add a title above the selector for the assigned/on hold/closed section
      headerTitle: t('chats.subtitle.myConversations'),
      value: assignedSegmentValue,
      onChange: value => setAssignedSegmentValue(value),
      content: assignedContent,
    });
  };

  const renderPatientWithConversations = () => (
    <div className="overflow-y-scroll">
      {renderUnassignedChats()}
      {renderAssignedChats()}

      <div className="mb-4 flex flex-col items-center px-2 py-3 text-center">
        <span className="m-0 p-1 text-subtle-foreground">{t('chats.dashboardView.topicToCover')}</span>
      </div>

      <div className="mb-4 flex flex-col items-center px-2 py-3 text-center">
        <h5 className="p-1 text-subtle-foreground">{t('chats.dashboardView.caregiversTeam')}</h5>
        <CaregiverAvatarList direction="horizontal" />
      </div>

      <div className="mt-4 flex flex-col p-2">
        <Button variant="secondary" size="lg" onClick={() => navigate(routes.messagingHowto.route)}>
          <InfoCircleOutlined />
          {t('chats.goToExplainer')}
        </Button>
      </div>
    </div>
  );

  const renderPatientNoConversations = () => (
    <div className="overflow-y-scroll">
      <ChatExplainer scrollableElement={scrollableElement} />
    </div>
  );

  const renderLoadingPage = () => (
    <div className="mb-4 flex flex-1 flex-col items-center justify-center px-2">
      <IconRound fill="currentColor" className="my-8 text-foreground" height={48} width={48} />
      <p>{t('chats.loadingPage.message')}</p>
      <Spinner className="size-6" />
    </div>
  );

  const renderCaregiver = () => (
    <>
      {renderUnassignedChats()}
      {renderAssignedChats()}
    </>
  );

  return (
    <Page>
      <PageTopBar title={t('chats.title')} />
      <PageContent className="gap-0 p-0" ref={scrollableElement} data-testid="messaging-home-scroll">
        {currentUser?.is_caregiver ? (
          renderCaregiver()
        ) : !loadedConversationsOnce ? (
          renderLoadingPage()
        ) : isMessagingExplainerV3Enabled() ? (
          <MessagingHomeV3 scrollableElement={scrollableElement} />
        ) : conversations.length !== 0 ? (
          renderPatientWithConversations()
        ) : (
          renderPatientNoConversations()
        )}
      </PageContent>
    </Page>
  );
}
